import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, ChevronDown, Trash2 } from "lucide-react";
import { api, coachHeaders, heureAff } from "../lib/api";

const Group = ({ title, items, color }) => (
  <div>
    <p className={`font-mono text-xs uppercase tracking-[0.15em] ${color}`}>{title} ({items.length})</p>
    <ul className="mt-1 text-sm text-chalk">
      {items.length === 0 ? <li className="text-dim">—</li> : items.map((t) => <li key={t}>{t}</li>)}
    </ul>
  </div>
);

const ArchiveItem = ({ a, onDelete }) => {
  const [open, setOpen] = useState(false);
  const m = a.match ?? {};
  return (
    <li data-testid="archive-item" className="py-2">
      <div className="flex items-center gap-2">
        <button data-testid={`archive-toggle-${a.id}`} onClick={() => setOpen(!open)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <ChevronDown className={`h-4 w-4 shrink-0 text-dim transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="min-w-0 truncate font-semibold text-chalk">{m.date} · {m.adversaire}</span>
        </button>
        <span className="shrink-0 font-mono text-[11px] text-lime">{a.presents?.length ?? 0} P · {a.absents?.length ?? 0} A</span>
        <button data-testid={`archive-delete-${a.id}`} onClick={() => onDelete(a)} aria-label="Supprimer le résumé" className="p-1 text-dim hover:text-signal"><Trash2 className="h-4 w-4" /></button>
      </div>
      {open && (
        <div data-testid={`archive-detail-${a.id}`} className="mt-2 grid gap-3 rounded-xl bg-ink p-3 sm:grid-cols-3">
          <Group title="Présents" items={a.presents ?? []} color="text-lime" />
          <Group title="Absents" items={(a.absents ?? []).map((x) => (x.motif ? `${x.nom} (${x.motif})` : x.nom))} color="text-signal" />
          <Group title="Pas répondu" items={a.sans_reponse ?? []} color="text-dim" />
          <p className="font-mono text-[11px] text-dim sm:col-span-3">{m.adresse || m.lieu} · RDV {heureAff(m.rendez_vous)} · clôturé par {a.cree_par} le {new Date(a.cree_le).toLocaleDateString("fr-FR")}</p>
        </div>
      )}
    </li>
  );
};

export const Archives = ({ code, convocationId, onArchived }) => {
  const [list, setList] = useState([]);
  const load = useCallback(() => {
    api.get("/archives", coachHeaders(code)).then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const cloturer = async () => {
    if (!convocationId) return;
    if (!window.confirm("Clôturer cette convocation ?\nUn résumé sera enregistré, puis la convocation et ses réponses seront supprimées.")) return;
    try { await api.post("/archives", {}, { ...coachHeaders(code), params: { convocation_id: convocationId } }); toast.success("Convocation clôturée, résumé enregistré"); load(); onArchived(); }
    catch { toast.error("Clôture impossible"); }
  };
  const remove = async (a) => {
    if (!window.confirm("Supprimer définitivement ce résumé ?")) return;
    await api.delete(`/archives/${a.id}`, coachHeaders(code));
    load();
  };

  return (
    <div data-testid="archives-section" className="rounded-2xl border border-line bg-pitch p-4">
      <h2 className="font-display text-2xl uppercase text-chalk">Convocations passées</h2>
      <button data-testid="archive-close-button" onClick={cloturer} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-lime px-5 py-3 font-semibold text-lime transition-colors hover:bg-lime hover:text-ink">
        <Archive className="h-4 w-4" /> Clôturer la convocation sélectionnée
      </button>
      <ul className="mt-2 divide-y divide-line">
        {list.length === 0 && <li data-testid="archives-empty" className="py-3 text-sm text-dim">Aucun résumé enregistré.</li>}
        {list.map((a) => <ArchiveItem key={a.id} a={a} onDelete={remove} />)}
      </ul>
    </div>
  );
};
